import { Column, Table } from '@tanstack/react-table';
import { TableCellDiv } from 'src/core/ui/TableCellDiv';
import { useColumnUserSetting } from 'src/hooks/useColumnUserSetting';
import { TableShimmeringDiv } from 'src/tables/Table';
import { getColumnPersonalization } from 'src/utils/columns/columnPersonalizationUtils';
import { applyNumberFormatting } from 'src/utils/numberFormatter';
import { SectionType } from 'src/utils/types/sectionType';
import { UiMoney } from 'src/WebApiController';

import * as styles from './FooterCell.css';

export function createMoneyFooter<T>(
  shimmeringDivHeight?: string,
  defaultSectionType?: SectionType,
  customAccessorFn?: (original: T | null, index: number) => UiMoney | null
) {
  return function Cell({
    table,
    column,
  }: {
    table: Table<T | null>;
    column: Column<T | null, unknown>;
  }) {
    const columnId = column.id;
    const original = table.options.meta?.summaryData;

    const { userDefinedPrecision } = useColumnUserSetting(
      columnId,
      table.options.meta?.sectionType ??
        defaultSectionType ??
        SectionType.Listings
    );

    if (!original) {
      return <TableShimmeringDiv height={shimmeringDivHeight} />;
    }

    const value = customAccessorFn
      ? customAccessorFn(original, -1)
      : (column.accessorFn?.(original, -1) as UiMoney | null);

    if (!value?.amt) {
      return null;
    }

    return (
      <TableCellDiv align="right" className={styles.bold}>
        {applyNumberFormatting({
          precision: userDefinedPrecision,
          inputNumber: value.amt,
          currencyCode: value.currency,
          currencyDecimalDigits: value.dec,
        })}
      </TableCellDiv>
    );
  };
}

export function createNumberFooter<T>(
  shimmeringDivHeight?: string,
  sectionType?: SectionType,
  customAccessorFn?: (original: T | null, index: number) => number | null,
  isPercentOverride?: boolean
) {
  return function Cell({
    table,
    column,
  }: {
    table: Table<T | null>;
    column: Column<T | null, unknown>;
  }) {
    const columnId = column.id;
    const original = table.options.meta?.summaryData;

    const { userDefinedPrecision } = useColumnUserSetting(
      columnId,
      sectionType ?? table.options.meta?.sectionType ?? SectionType.Listings
    );

    const { isPercent } = getColumnPersonalization({
      id: columnId,
      sectionType:
        sectionType ?? table.options.meta?.sectionType ?? SectionType.Listings,
    });

    if (!original) {
      return <TableShimmeringDiv height={shimmeringDivHeight} />;
    }

    const value = customAccessorFn
      ? customAccessorFn(original, -1)
      : (column.accessorFn?.(original, -1) as number | null);

    if (value == null) {
      return null;
    }

    return (
      <TableCellDiv align="right" className={styles.bold}>
        {value != null && !isNaN(value)
          ? applyNumberFormatting({
              precision: userDefinedPrecision,
              inputNumber: value,
              isPercent: isPercentOverride ?? isPercent,
            })
          : null}
      </TableCellDiv>
    );
  };
}
